import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";

interface Location {
  id: string;
  name: string;
  type: "hangar" | "ramp";
  tripCount?: number;
}

interface LocationFilterProps {
  locations: Location[];
  selectedLocationIds: string[];
  onChange: (locationIds: string[]) => void;
  disabled?: boolean;
}

export const LocationFilter: React.FC<LocationFilterProps> = ({
  locations,
  selectedLocationIds,
  onChange,
  disabled = false,
}) => {
  // Group locations by type for better organization
  const groupedOptions = React.useMemo(() => {
    const hangars = locations.filter((loc) => loc.type === "hangar");
    const ramps = locations.filter((loc) => loc.type === "ramp");

    const options: any[] = [];

    if (hangars.length > 0) {
      options.push({ group: "Hangars", locations: hangars });
    }

    if (ramps.length > 0) {
      options.push({ group: "Ramps", locations: ramps });
    }

    return options;
  }, [locations]);

  // Flatten locations for autocomplete
  const allLocations = React.useMemo(() => {
    return locations.map(loc => ({
      ...loc,
      label: `${loc.name}${loc.tripCount ? ` (${loc.tripCount})` : ""}`,
      group: loc.type === "hangar" ? "Hangars" : "Ramps"
    }));
  }, [locations]);

  // Get selected location objects
  const selectedLocations = React.useMemo(() => {
    return allLocations.filter((loc) => selectedLocationIds.includes(loc.id));
  }, [allLocations, selectedLocationIds]);

  const handleChange = (_event: any, newValue: any[]) => {
    onChange(newValue.map((loc) => loc.id));
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disabled={disabled}
      options={allLocations}
      groupBy={(option) => option.group}
      value={selectedLocations}
      onChange={handleChange}
      size="small"
      sx={{
        minWidth: 250,
        maxWidth: 400,
        "& .MuiAutocomplete-tag": {
          maxWidth: "120px",
        }
      }}
      ListboxProps={{
        style: {
          maxHeight: "none",
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label="Filter by Location"
          placeholder={selectedLocationIds.length === 0 ? "All locations" : ""}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...restProps } = props;
        return (
          <Box component="li" key={key} {...restProps}>
            <ListItemText
              primary={
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Typography variant="body2">{option.name}</Typography>
                  {option.tripCount !== undefined && (
                    <Typography variant="caption" color="text.secondary">
                      {option.tripCount}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Box>
        );
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => {
          const { key, ...restProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              {...restProps}
              label={option.name}
              size="small"
              color={option.type === "hangar" ? "primary" : "secondary"}
              variant="outlined"
            />
          );
        })
      }
      ChipProps={{
        size: "small",
        variant: "outlined",
      }}
    />
  );
};